import { useSyncExternalStore } from "react";
import { istInstalliert } from "../schale/geraet";

/* ══════════════════════════════════════════════════════════════════
   „Zum Startbildschirm hinzufügen" — einmal, und dann nie wieder.

   Am Telefon ist das der Unterschied zwischen einer Adresse, die jemand
   tippt, und einem Symbol, das dasteht. Ohne das Symbol gibt es auch
   keinen eigenen Rahmen, keinen Vollbildstart und — auf einem iPhone —
   keinen verlässlichen Offline-Speicher.

   **Zwei Browser, zwei Wege.**

   - **Chrome und die Übrigen** melden `beforeinstallprompt`. Der Knopf
     ruft dann `prompt()` auf, und der Browser fragt selbst. Das Ereignis
     muss dafür aufgehoben werden — es kommt genau einmal.
   - **Safari auf dem iPhone meldet nichts** und bietet nichts an. Dort
     bleibt nur der Satz „Teilen → Zum Home-Bildschirm". Er steht in
     `de.json`, nicht hier.

   **Einmal und wegklickbar.** Ein Hinweis, der jeden Morgen wiederkommt,
   ist ein Hinweis, den man wegwischt, ohne ihn zu lesen — und danach
   wischt man auch das Band mit der Warteschlange weg. Deshalb merkt sich
   `localStorage`, dass er weg ist. Das ist eine Bequemlichkeit je Gerät
   und kein Datenbestand: Geht der Eintrag verloren, kommt der Hinweis
   noch einmal, und mehr passiert nicht.
   ══════════════════════════════════════════════════════════════════ */

const MARKE = "machine-office:installhinweis-weg";

/** Wie der Hinweis aussieht — oder `null`, wenn keiner hingehört. */
export type Installlage = "knopf" | "anleitung";

type Aufforderung = Event & { prompt: () => Promise<void> };

let aufforderung: Aufforderung | null = null;
let weggeklickt = gemerkt();
const hoerer = new Set<() => void>();

function gemerkt(): boolean {
  try {
    return localStorage.getItem(MARKE) === "1";
  } catch {
    // Ein privates Fenster wirft hier. Dann steht der Hinweis eben wieder da.
    return false;
  }
}

function melden(): void {
  hoerer.forEach((h) => h());
}

function abonnieren(hoer: () => void): () => void {
  hoerer.add(hoer);
  return () => hoerer.delete(hoer);
}

/* „Läuft sie schon installiert?" fragt `schale/geraet.ts` (`istInstalliert`).
   Bis zum 17.09.2026 stand hier eine zweite Antwort, die nur `standalone`
   kannte — `geraet.ts` kennt auch `minimal-ui` und `fullscreen`. Zwei
   Antworten auf dieselbe Frage hätten auf einem Gerät den Installhinweis
   gezeigt, das die Lage schon für installiert hielt
   (40-mobiles-grundgeruest.md, „Installiert? hat zwei Antworten"). */

/** Ein iPhone oder iPad — dort gibt es nur die Anleitung. */
function istApfelTelefon(): boolean {
  const kennung = navigator.userAgent;
  if (/iPhone|iPod/.test(kennung)) return true;
  // Ein iPad meldet sich seit iPadOS 13 als Mac; der Berührungsschirm verrät es.
  return /Macintosh/.test(kennung) && navigator.maxTouchPoints > 1;
}

function lage(): Installlage | null {
  if (weggeklickt || istInstalliert()) return null;
  if (aufforderung) return "knopf";
  return istApfelTelefon() ? "anleitung" : null;
}

/**
 * Was für ein Hinweis hingehört — `null` heißt: keiner.
 *
 * **Der Schirm entscheidet nicht selbst, ob es ein Telefon ist.** Das tut
 * `schale/geraet.ts`; der Hinweis steht in der Handy-Maske und damit von
 * vornherein dort, wo er hingehört.
 */
export function useInstallhinweis(): Installlage | null {
  return useSyncExternalStore(abonnieren, lage, () => null);
}

/** Der Knopf: den Browser fragen lassen. Danach ist der Hinweis erledigt. */
export function installierenAnbieten(): void {
  const jetzt = aufforderung;
  if (!jetzt) return;
  aufforderung = null;
  void jetzt.prompt().finally(() => {
    // Ob angenommen oder abgelehnt — gefragt wurde, und ein zweites Mal
    // fragt der Browser ohnehin nicht von selbst.
    hinweisWegklicken();
  });
}

export function hinweisWegklicken(): void {
  weggeklickt = true;
  try {
    localStorage.setItem(MARKE, "1");
  } catch {
    // Ohne Gedächtnis kommt der Hinweis beim nächsten Öffnen wieder. Das ist
    // der ganze Schaden.
  }
  melden();
}

export function installhinweisHorchen(): void {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Ohne `preventDefault` zeigt Chrome sein eigenes Band; wir wollen den
    // Hinweis dort, wo der Fahrer ohnehin hinsieht.
    e.preventDefault();
    aufforderung = e as Aufforderung;
    melden();
  });
  window.addEventListener("appinstalled", () => {
    aufforderung = null;
    hinweisWegklicken();
  });
}
